import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CurrencyGbp, Ticket, Eye, Info } from "@phosphor-icons/react";
import { formatGBPDecimal, formatNumber } from "../lib/api";
import { computeBudgetOddsView } from "../lib/budgetOdds";
import { currentTicketPrice, sanitisePublicBudget } from "../lib/publicCompetitionContract";

/**
 * Compact Budget Odds module rendered on the competition detail page above
 * "What the numbers say". It mirrors `/api/budget-odds`: ticket price,
 * trusted total ticket cap, and explicit per-user limits only.
 */
const QUICK_PRESETS = [5, 10, 20, 50, 100];

export default function CompetitionBudgetModule({ comp }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialBudget = sanitisePublicBudget(searchParams.get("budget"), 20);
  const [budget, setBudget] = useState(initialBudget);
  const [budgetInput, setBudgetInput] = useState(String(initialBudget));

  const ticketPrice = currentTicketPrice(comp) || 0;
  const compScore = comp.scores?.overall_compcheck_score;
  const compBand = comp.scores?.score_band;

  const view = useMemo(
    () => computeBudgetOddsView(comp, budget),
    [budget, comp]
  );

  useEffect(() => {
    const fromUrl = sanitisePublicBudget(searchParams.get("budget"), 20);
    setBudget(fromUrl);
    setBudgetInput(String(fromUrl));
  }, [searchParams]);

  const setBudgetWithUrl = (value) => {
    const v = sanitisePublicBudget(value, 20);
    setBudget(v);
    setBudgetInput(String(v));
    const next = new URLSearchParams(searchParams);
    next.set("budget", String(v));
    setSearchParams(next);
  };

  const applyBudgetInput = () => {
    setBudgetWithUrl(budgetInput);
  };

  const bandColor =
    compBand === "Strong" || compBand === "Good" ? "#047857" :
    compBand === "Mixed" ? "#92400e" :
    "#991b1b";

  const ticketCountText =
    view.tickets != null
      ? `${view.tickets} ticket${view.tickets === 1 ? "" : "s"}`
      : "-";

  return (
    <div
      className="rounded-lg bg-white p-6 border border-slate-200"
      data-testid="detail-budget-module"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="overline text-[var(--cc-text-dark-muted)] mb-1 inline-flex items-center gap-1.5">
            <CurrencyGbp size={12} weight="fill" /> Budget odds for this competition
          </div>
          <p className="text-xs text-[var(--cc-text-dark-muted)] max-w-md leading-relaxed">
            See what your budget really buys. Numbers-first, no winning promises.
          </p>
        </div>
        {compScore != null && (
          <div
            className="text-right"
            data-testid="detail-budget-module-compcheck"
          >
            <div className="overline text-[var(--cc-text-dark-muted)]">CompCheck</div>
            <div className="font-mono-num text-2xl font-semibold text-[var(--cc-text-dark)] leading-none">
              {compScore}
            </div>
            {compBand && (
              <div
                className="text-[10px] font-semibold uppercase tracking-wider mt-1"
                style={{ color: bandColor }}
              >
                {compBand}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center bg-slate-50 border border-slate-200 rounded px-2 focus-within:border-[var(--cc-bronze)]">
          <CurrencyGbp size={14} className="text-slate-400" />
          <input
            type="number"
            min="1"
            max="100000"
            value={budgetInput}
            onChange={(e) => setBudgetInput(e.target.value)}
            onBlur={applyBudgetInput}
            onKeyDown={(e) => e.key === "Enter" && applyBudgetInput()}
            data-testid="detail-budget-input"
            className="bg-transparent text-[var(--cc-text-dark)] text-sm font-mono-num py-1.5 px-1 w-20 outline-none"
          />
        </div>
        {QUICK_PRESETS.map((b) => (
          <button
            key={b}
            type="button"
            onClick={() => setBudgetWithUrl(b)}
            data-testid={`detail-budget-preset-${b}`}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
              budget === b
                ? "bg-[var(--cc-navy)] border-[var(--cc-navy)] text-white"
                : "border-slate-300 text-slate-600 hover:border-slate-500"
            }`}
          >
            {"\u00a3"}{b}
          </button>
        ))}
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat
          icon={Ticket}
          label="Budget buys"
          value={ticketCountText}
          sub={view.cappedByUserLimit ? "capped by per-user limit" : null}
          testId="detail-budget-tickets"
        />
        <Stat
          icon={Eye}
          label="Estimated maximum-pool odds"
          value={view.ok ? view.oddsText : "Odds unavailable"}
          highlight={view.ok}
          testId="detail-budget-odds"
        />
        <Stat
          icon={Ticket}
          label="Ticket price"
          value={ticketPrice > 0 ? formatGBPDecimal(ticketPrice) : "Unknown"}
          testId="detail-budget-ticket-price"
        />
        <Stat
          icon={Ticket}
          label="Ticket cap"
          value={view.totalTicketCap ? formatNumber(view.totalTicketCap) : "Unknown"}
          testId="detail-budget-cap"
        />
      </div>

      <p className="mt-3 text-xs text-[var(--cc-text-dark-muted)]" data-testid="detail-budget-price-basis">
        {view.bulkDiscountApplied
          ? "Bulk discount applied where explicitly available."
          : "Uses the current live ticket price; no trusted bulk discount found."}
      </p>

      {view.ok && (
        <p className="mt-3 text-xs text-[var(--cc-text-dark-muted)]" data-testid="detail-budget-pool-basis">
          Assumes the full ticket cap is sold; ticket sales do not change this estimate.
        </p>
      )}

      {!view.ok && view.reason && (
        <p
          className="mt-3 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-2"
          data-testid="detail-budget-warning"
        >
          {view.reason}
        </p>
      )}

      <p
        className="mt-4 text-[11px] text-[var(--cc-text-dark-muted)] leading-relaxed inline-flex items-start gap-1.5"
        data-testid="detail-budget-disclaimer"
      >
        <Info size={11} weight="fill" className="mt-0.5 flex-none" />
        Estimated only. Always check the operator terms.
      </p>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, sub, highlight, testId }) => (
  <div
    className={`rounded p-2 ${highlight ? "bg-[var(--cc-navy)] text-white" : "bg-slate-50 text-[var(--cc-text-dark)]"}`}
    data-testid={testId}
  >
    <div
      className={`overline text-[9px] inline-flex items-center gap-1 ${
        highlight ? "text-white/60" : "text-[var(--cc-text-dark-muted)]"
      }`}
    >
      <Icon size={10} weight="fill" /> {label}
    </div>
    <div className="font-mono-num text-sm font-semibold mt-0.5">{value}</div>
    {sub && (
      <div
        className={`text-[10px] mt-0.5 ${
          highlight ? "text-white/60" : "text-[var(--cc-text-dark-muted)]"
        }`}
      >
        {sub}
      </div>
    )}
  </div>
);
